import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FormProvider, useForm } from "react-hook-form";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import { useStateContext } from "../../../contexts/auth-context";

import { yupResolver } from "@hookform/resolvers/yup";

import SalesOrderTab from "./SalesOrderTab";
import DocumentInfoTab from "./DocumentInfoTab";
import { Box, CircularProgress, IconButton } from "@mui/material";
import CustomizedTab from "../../../components/Custom/CustomizedTab";
import { CustomizedTooltip } from "../../../components/Custom/CustomizedTooltip";
import CustomizedBreadcrumbs from "../../../components/Custom/CustomizedBreadcrumbs";
import { ActivityLogSlideInPanel } from "../../../components/UI/SlideInPanel/ActivityLogSlideInPanel";
import {
  quotationSchema,
  quotationValidation,
} from "../../../components/Form/Sales/Quotation/schema";

import {
  ActivityLogDocumentType,
  QuotationFindUniqueQuery,
  useQuotationFindUniqueQuery,
} from "../../../generated/sales";
import RestoreOutlinedIcon from "@mui/icons-material/RestoreOutlined";

import { IQuotation } from "../../../types/Sales/quotation";
import { IBreadcrumbsAndMenu, ITab } from "../../../types/global";

import { createGraphQLClientWithMiddleware } from "../../../services/graphqlClient";

const QuotationContainer = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const tab = searchParams.get("tab");
  const navigate = useNavigate();

  const {
    state: { authUser, permissions },
  } = useStateContext();

  const [openDrawer, setOpenDrawer] = useState<boolean>(false);

  const { id } = useParams();

  const tabs: ITab[] = [
    {
      label: t("sales.document_info"),
      path: `${pathname}`,
    },
    {
      label: t("sales.order.index"),
      path: `${pathname}?tab=sales_order`,
    },
  ];

  const graphQLClient = createGraphQLClientWithMiddleware("sales");

  const { data, isLoading, isSuccess } =
    useQuotationFindUniqueQuery<QuotationFindUniqueQuery>(
      graphQLClient,
      {
        uniqueInput: {
          id: id ? parseInt(id) : undefined,
        },
      },
      {
        enabled: !!id,
        cacheTime: 0,
      }
    );

  const renderTab = (tab: string | null) => {
    switch (tab) {
      case "sales_order":
        return (
          <SalesOrderTab
            referenceUniqueId={data?.QuotationFindUnique.unique_id}
          />
        );
      default:
        return <DocumentInfoTab />;
    }
  };

  const currentTab = pathname + (tab ? `?tab=${tab}` : "");

  const branch_id = sessionStorage.getItem("branch-id");

  const methods = useForm<IQuotation>({
    defaultValues: {
      ...quotationSchema,
      branch_id: branch_id ? parseInt(branch_id) : undefined,
    },
    resolver: yupResolver<any>(quotationValidation),
  });

  const breadcrumbs: IBreadcrumbsAndMenu[] = [
    {
      name: t("sales.index"),
      to: "/sales",
    },
    {
      name: t("sales.quotation.index"),
      to: "/sales/quotation",
    },
    {
      name: id
        ? data?.QuotationFindUnique.unique_id || "-"
        : t("button.create") + t("sales.quotation.index"),
    },
  ];

  const status = data?.QuotationFindUnique.aggrid_status;

  useEffect(() => {
    if (isSuccess && permissions?.quotation?.view_self) {
      const createdBy = data?.QuotationFindUnique?.created_by;
      const relatedUserList = data?.QuotationFindUnique?.related_user_list;
      if (
        createdBy?.user_unique_id === authUser?.unique_id ||
        relatedUserList?.find(
          (user) => user.user_unique_id === authUser?.unique_id
        )
      ) {
        return;
      } else {
        navigate(`/unauthorized`, {
          replace: true,
        });
      }
    }
  }, [
    authUser?.unique_id,
    data?.QuotationFindUnique?.created_by,
    data?.QuotationFindUnique?.related_user_list,
    isSuccess,
    navigate,
    permissions?.quotation?.view_self,
  ]);

  if (id && isLoading) {
    return (
      <Box
        sx={{
          height: "calc(100dvh - 176px)",
          marginRight: "260px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
  return (
    <FormProvider {...methods}>
      <Box display={"flex"} justifyContent={"space-between"} maxWidth={1040}>
        <CustomizedBreadcrumbs breadcrumbs={breadcrumbs} />
        {id && (
          <CustomizedTooltip title="ดูความเคลื่อนไหว" enterNextDelay={200}>
            <IconButton
              onClick={() => {
                setOpenDrawer(true);
              }}
              sx={{
                color: (theme) => theme.palette.primary.main,
                padding: 0,
                marginLeft: "4px",
              }}
            >
              <RestoreOutlinedIcon />
            </IconButton>
          </CustomizedTooltip>
        )}
      </Box>
      {(status === "accepted" || status === "finished") && (
        <CustomizedTab tabs={tabs} currentTab={currentTab} />
      )}
      {renderTab(tab)}
      {id && (
        <ActivityLogSlideInPanel
          open={openDrawer}
          handleClose={() => setOpenDrawer(false)}
          documentId={id!}
          documentType={ActivityLogDocumentType.Quotation}
        />
      )}
    </FormProvider>
  );
};

export default QuotationContainer;
